import type { FunctionComponent } from "react"

import { usePathname, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

import { TagCTA } from "@jobteaser/ui-kit/components/Tag"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { ARTICLE_SUBJECT_TYPES_API } from "@/modules/companies/constants/enums"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useRouter } from "@/modules/routing/hooks/useRouter"

import styles from "./ArticlesFilters.module.css"

export const ArticlesFilters: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const { push } = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const articlesSubjectQuery = searchParams.get("articlesSubject") || undefined
  const { dataCareerCenter } = useCareerCenterFetch()

  const getQueryParamSubject = useCallback((): string | undefined => {
    let articlesSubject: string | undefined

    if (articlesSubjectQuery) {
      articlesSubject = articlesSubjectQuery
    }
    return articlesSubject
  }, [articlesSubjectQuery])

  const [currentFilter, setCurrentFilter] = useState(getQueryParamSubject())
  const [hasExclusiveFilter, setHasExclusiveFilter] = useState(!!searchParams.get("articlesExclusive"))

  const setFilter = (filter: string): void => {
    const updatedSearchParams = new URLSearchParams(searchParams)

    if (currentFilter === filter) {
      setCurrentFilter("")
      updatedSearchParams.delete("articlesSubject")
    } else {
      setCurrentFilter(filter)
      updatedSearchParams.set("articlesSubject", filter)
    }

    const search = updatedSearchParams.toString()

    push(`${pathname}${search ? `?${search}` : ""}`, undefined, { scroll: false, shallow: true })
  }

  const setExclusiveFilter = (): void => {
    const updatedSearchParams = new URLSearchParams(searchParams)

    if (hasExclusiveFilter) {
      setHasExclusiveFilter(false)
      updatedSearchParams.delete("articlesExclusive")
    } else {
      setHasExclusiveFilter(true)
      updatedSearchParams.set("articlesExclusive", "true")
    }

    const search = updatedSearchParams.toString()

    push(`${pathname}${search ? `?${search}` : ""}`, undefined, { scroll: false, shallow: true })
  }

  useEffect(() => {
    if (currentFilter !== articlesSubjectQuery) {
      setCurrentFilter(getQueryParamSubject())
    }
  }, [currentFilter, getQueryParamSubject, articlesSubjectQuery])

  const showExclusiveFilter = dataCareerCenter?.intranet

  return (
    <ul className={styles.wrapper}>
      {showExclusiveFilter && (
        <li>
          <TagCTA as="button" isSelected={hasExclusiveFilter} onClick={() => setExclusiveFilter()}>
            {t("companies_client.newsfeed.articles.filter.exclusive", { name: dataCareerCenter?.name || "" })}
          </TagCTA>
        </li>
      )}
      {Object.entries(ARTICLE_SUBJECT_TYPES_API).map(type => {
        const key = type[0]
        return (
          <li key={key}>
            <TagCTA as="button" isSelected={currentFilter === key} onClick={() => setFilter(key)}>
              {t(`companies_client.articles_type.${key}`)}
            </TagCTA>
          </li>
        )
      })}
    </ul>
  )
}
